<template>
  <div class="ba-slider"
       :class="{ minimal, simple, vertical, disabled }"
       @mouseover="setHover(true)"
       @mouseleave="setHover(false)">
    <button class="plus"
            v-if="!simple"
            @click="plus">
      <span class="icon icon--plus"></span>
    </button>
    <div v-if="!minimal"
         class="slider"
         :class="{ moving: moving }"
         :style="{ width: !vertical ? sliderWidth : null,
                   height: vertical ? sliderWidth : null }"
         @mouseup="setMoving(false)"
         @mousedown="setMoving(true)">
      <span class="pin"
            v-if="!disabled"
            :style="pinStyle">
        <span class="direct-input"
              v-if="!simple"
              :class="{ active: directInputing }">
          <input type="text"
                 v-model="directValue"
                 @blur="setDirectInputing(false)"
                 @focus="setDirectInputing(true)"
                 @keyup.enter="setDirectInputing(true)">
        </span>
      </span>
      <span class="active-tick"
            v-if="simple && !disabled"
            :style="{ width: !vertical ? `${(value - min) / rangeList.length * 100}%` : null,
                   height: vertical ? `${(value - min) / rangeList.length * 100}%` : null }"></span>
      <span v-for="v in rangeList"
            class="tick"
            :style="{ width: !vertical ? `${1 / rangeList.length * 100}%` : null,
                   height: vertical ? `${1 / rangeList.length * 100}%` : null }"
            :key="`range${v}`"
            @click="onSliderValue(v, false)"
            @mouseenter="onSliderValue(v)"></span>
    </div>
    <div v-else
          class="direct-input"
          :class="{ active: directInputing }">
      <input type="text"
             v-model="directValue"
             @blur="setDirectInputing(false)"
             @focus="setDirectInputing(true)"
             @keyup.enter="setDirectInputing(true)">
    </div>
    <button class="minus"
            v-if="!simple"
            @click="minus">
      <span class="icon icon--minus"></span>
    </button>
    <slot/>
  </div>
</template>

<script>
export default {
  name: 'BaSlider',
  props: {
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    sliderWidth: {
      default: null
    },
    default: {
      type: Number,
      default: 50
    },
    sliderStep: {
      type: Number,
      default: 1
    },
    buttonStep: {
      type: Number,
      default: 1
    },
    value: {
      type: Number
    },
    minimal: {
      type: Boolean,
      default: false
    },
    simple: {
      type: Boolean,
      default: false
    },
    vertical: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  created () {
    if (this.value) {
      this.realValue = this.value
    } else {
      this.realValue = this.default
    }
    for (let idx = this.range; idx >= 0; idx--) {
      this.rangeList.push(
        this.min + idx
      )
    }
  },
  watch: {
    value (value) {
      this.realValue = value
    },
    realValue (value) {
      this.setDirectValue(value)
    }
  },
  computed: {
    pinStyle () {
      if (this.vertical) {
        return { top: 100 - (this.realValue - this.min) / this.range * 100 + '%' }
      } else {
        return { left: (this.realValue - this.min) / this.range * 100 + '%' }
      }
    },
    range () {
      return Math.abs(this.max - this.min)
    },
    magnetValue () {
      return {
        min: this.default * 0.95,
        max: this.default * 1.05
      }
    }
  },
  methods: {
    constraint (value) {
      let realValue = Math.ceil(value / this.sliderStep) * this.sliderStep
      if (realValue < this.min) {
        realValue = this.min
      } else if (realValue > this.max) {
        realValue = this.max
      }
      return realValue
    },
    setValue (value) {
      if (!this.disabled) {
        if (isNaN(value)) {
          value = this.realValue
        }
        this.realValue = this.constraint(value)
        this.$emit('change', this.realValue)
      }
    },
    minus () {
      let realValue = Math.ceil(this.realValue / this.buttonStep) * this.buttonStep
      this.setValue(realValue - this.buttonStep)
    },
    plus () {
      let realValue = Math.floor(this.realValue / this.buttonStep) * this.buttonStep
      this.setValue(realValue + this.buttonStep)
    },
    onSliderValue (value, dragging = true) {
      if (this.moving || !dragging) {
        value = Math.floor(value)
        if (value >= this.magnetValue.min && value <= this.magnetValue.max) {
          value = this.default
        }
        this.setValue(value)
      }
    },
    setMoving (value) {
      this.moving = value
    },
    setDirectValue (value) {
      this.directValue = parseInt(value)
      if (this.directValue !== 0 && !this.directValue) {
        this.directValue = this.default
      }
      this.directValue = this.constraint(this.directValue)
      this.setValue(parseInt(this.directValue))
      if (!this.directInputing && !this.minimal) {
        this.directValue += '%'
      }
    },
    setDirectInputing (value) {
      this.directInputing = value
      this.setDirectValue(this.directValue)
    },
    setHover (value) {
      this.hover = value
      if (!value) {
        this.moving = false
      }
    }
  },
  data () {
    return {
      realValue: null,
      directValue: null,
      directInputing: false,
      rangeList: [],
      moving: false,
      hover: false
    }
  }
}
</script>

<style lang="scss" scoped>
.ba-slider {
  display: inline-block;
  vertical-align: middle;
  position: relative;
  border-radius: 5px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
  background-color: #ffffff;
  height: 22px;
  padding: 5px;
  transition: opacity 0.2s ease;

  &.disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  .slider {
    position: relative;
    display: inline-block;
    vertical-align: middle;
    width: 40px;
    height: 22px;
    margin: 0 10px;
    float: right;

    .pin {
      @include positionXYcenter;
      z-index: 2;
      width: 12px;
      height: 12px;
      border-radius: 6px;
      box-shadow: 0 3px 3px -1px rgba(0, 0, 0, 0.28), 0 0 0 1px rgba(0, 0, 0, 0.18);
      background-color: #ffffff;
      transition: left 0.3s cubic-bezier(.28,.12,.42,.94);

      .direct-input {
        @include positionXcenter;
        top: -30px;
        width: 36px;
        height: 16px;
        padding: 3px 4px 4px 4px;
        text-align: center;
        box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16);
        background-color: #292929;
        border-radius: 4px;
        opacity: 0;
        transition: opacity 0.3s ease;

        &:after {
          content: "";
          @include positionXcenter;
          bottom: -4px;
          width: 0;
          height: 0;
          border-left: 4px solid transparent;
          border-right: 4px solid transparent;
          border-top: 4px solid #292929;
        }

        input {
          background: transparent;
          border: 1px solid transparent;
          text-align: center;
          width: 32px;
          height: 13px;
          line-height: 14px;
          border-radius: 1px;
          font-size: 12px;
          color: #bbbbbb;

          &:focus {
            border: solid 1px #9c9fe0;
            background-color: #3c3e6b;
          }
        }

        &.active {
          opacity: 1;
        }
      }
    }

    &:before {
      @include positionYcenter;
      left: -5px;
      z-index: 1;
      content: "";
      width: calc(100% + 10px);
      height: 2px;
      border-radius: 1px;
      background-color: #d8d8d8;
    }

    .active-tick {
      @include positionYcenter;
      display: block;
      left: -5px;
      z-index: 1;
      content: "";
      min-width: 2px;
      width: calc(100% + 10px);
      height: 2px;
      border-radius: 4px;
      background-color: #9fce12;
      transition: width 0.3s cubic-bezier(.28,.12,.42,.94);
    }

    .tick {
      position: relative;
      z-index: 3;
      display: inline-block;
      vertical-align: middle;
      height: 100%;
      width: 1px;
      cursor: pointer;
      float: right;
    }

    &.moving {
      .active-tick {
        transition: none;
      }

      .pin {
        transition: none;
      }
    }
  }

  button.plus,
  button.minus {
    position: relative;
    display: inline-block;
    vertical-align: middle;
    border-radius: 2px;
    transition: background-color 0.2s ease;
    width: 22px;
    height: 22px;
    float: right;

    .icon {
      @include positionXYcenter;
    }

    &:hover {
      background-color: rgba(0, 0, 0, 0.06);
    }

    &:active {
      background-color: rgba(0, 0, 0, 0.15);
    }
  }

  &:hover {
    .slider {
      .pin {
        .direct-input {
          opacity: 1;
        }
      }
    }
  }

  &.minimal {
    .direct-input {
      display: inline-block;
      vertical-align: middle;
      margin: 0 5px;

      input {
        text-align: center;
        width: 24px;
        height: 18px;
        border-radius: 2px;
        border: 1px solid #e2e2e2;
        background-color: #f6f6f6;
        line-height: 18px;
        font-size: 11px;
        color: #767676;
      }
    }
  }

  &.simple {
    padding: 0;
    box-shadow: none;
    background: transparent;

    .slider {
      &:before {
        border-radius: 4px;
        height: 8px;
      }

      .active-tick {
        min-width: 8px;
        height: 8px;
      }

      .pin {
        width: 18px;
        height: 18px;
        border-radius: 9px;
        box-shadow: 0 0 1px 0 rgba(0, 0, 0, 0.29), 0 2px 3px 0 rgba(0, 0, 0, 0.22);
      }
    }
  }

  &.vertical {
    width: 22px;
    height: auto;

    .slider {
      width: 22px;
      height: 40px;
      margin: 10px 0;
      float: none;

      &:before {
        @include positionXcenter;
        top: -5px;
        content: "";
        width: 2px;
        height: calc(100% + 10px);
      }

      .tick {
        display: block;
        width: 100%;
        height: 1px;
        float: none;
      }
    }

    button.plus,
    button.minus {
      float: none;
    }

    &.minimal {
      .direct-input {
        margin: 4px -2px;

        input {
          padding: 0;
          height: 16px;
          line-height: 16px;
        }
      }
    }

    &.simple {
      .slider {
        &:before {
          width: 8px;
        }
      }
    }
  }
}
</style>
